import type { ComponentType } from 'react'
import { useMemo } from 'react'

import type {
  ExtendedAutocompleteInputProps,
  InputProps,
  InputVariantsProps
} from './types'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import type { Theme, ThemeUICSSObject } from '../../../theme-provider/types'
import { isSafari } from '../../../utils/browser'
import { EMPTY_OBJECT, pick } from '../../../utils/object'
import { BoxPrivate } from '../box/box'
import type { BoxProps } from '../box/types'

const useExtractProps = (
  props: InputProps &
    ExtendedAutocompleteInputProps & {
      theme?: Theme
    }
) =>
  useMemo(() => {
    const {
      a11yId,
      a11yLabel,
      after,
      afterProps = EMPTY_OBJECT,
      autoComplete = 'one-time-code', // https://stackoverflow.com/questions/15738259/disabling-chrome-autofill#:~:text=June%202023%3A%20autocomplete%3D%22one%2Dtime%2Dcode%22%20works%20in%20Chrome
      before,
      beforeProps = EMPTY_OBJECT,
      className,
      defaultValue,
      disableBorderEffect,
      disabled,
      height = '2em',
      hidableTags,
      inline,
      isLoading,
      isRequired,
      maskOptions,
      message,
      messageTextProps,
      mode,
      onChange,
      placeholder,
      readOnly,
      rootComponent: Component = BoxPrivate,
      rootProps = EMPTY_OBJECT as BoxProps,
      showFocusStyle,
      size,
      state,
      sx = EMPTY_OBJECT as ThemeUICSSObject,
      tagsVariant,
      type = 'text',
      value: valueProp,
      variant,
      name = isSafari ? '__search__' : undefined, // https://bytes.grubhub.com/disabling-safari-autofill-for-a-single-line-address-input-b83137b5b1c7
      color,
      autoWidthPadding,
      ...rest
    } = props

    const maxWidthProps = {
      ...pick(props, ['maxWidth']),
      ...pick(sx, ['maxWidth'])
    }
    return {
      a11yId,
      a11yLabel,
      after,
      afterProps,
      autoComplete,
      before,
      beforeProps,
      className,
      Component: Component as ComponentType<
        BoxProps &
          PrivateThemeUiProps &
          InputVariantsProps &
          ExtendedAutocompleteInputProps & { __inputProps?: ThemeUICSSObject }
      >,
      defaultValue,
      disableBorderEffect,
      disabled,
      height,
      hidableTags,
      inline,
      isLoading,
      isRequired,
      maskOptions,
      message,
      messageTextProps,
      mode,
      onChange,
      placeholder,
      readOnly,
      rootProps: {
        ...maxWidthProps,
        ...rootProps
      },
      showFocusStyle,
      size,
      state,
      sx,
      tagsVariant,
      type,
      valueProp,
      variant,
      name,
      color,
      autoWidthPadding,
      rest
    }
  }, [props])

export default useExtractProps
